import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getEmailSettings, updateEmailSettings, sendTestEmail } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import {
    CheckCircle2,
    MinusCircle,
    Save,
    X,
    FlaskConical,
    Eye,
    EyeOff,
    RotateCcw,
    AlertTriangle,
} from 'lucide-react';

// Default subject/body templates — mirror backend/models/email.py
// DEFAULT_SUBJECT_TEMPLATE / DEFAULT_BODY_TEMPLATE exactly (Phase 3.3).
// Duplicated as frontend constants (rather than a round-trip to the backend)
// so "Reset to defaults" (Finding 5.64b) works without a dedicated API call.
// If the backend defaults ever change, update both copies together.
const DEFAULT_SUBJECT_TEMPLATE = 'Reminder: submit your {{month}} {{year}} report';

const DEFAULT_BODY_TEMPLATE = `<p>Hej {{user_name}},</p>

<p>This is a friendly reminder to submit your time report and expense report
for <strong>{{month}} {{year}}</strong>.</p>

<p><em>If you've already submitted, please disregard this message.</em></p>

<p>
  <a href="{{submit_url}}"
     style="background:#1f2937;color:#fff;padding:10px 20px;
            text-decoration:none;border-radius:4px;display:inline-block;">
    Open Hisland Time
  </a>
</p>

<p style="color:#6b7280;font-size:12px;">
  / Hisland Time — sent automatically on the last working day of each month.
</p>
`;

// Sample values used only for the admin-facing live preview (Finding 5.64a).
// submit_url mirrors the real SUBMIT_URL constant in
// backend/routes/email_settings.py / backend/routes/internal.py.
const PREVIEW_SAMPLE_USER_NAME = 'Anna Svensson';
const PREVIEW_SUBMIT_URL = 'https://time.hisland.com/dashboard/log-reports';

function renderTemplatePreview(template) {
    const now = new Date();
    const replacements = {
        user_name: PREVIEW_SAMPLE_USER_NAME,
        month: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(now),
        year: String(now.getFullYear()),
        submit_url: PREVIEW_SUBMIT_URL,
    };
    return Object.entries(replacements).reduce(
        (acc, [key, value]) => acc.split(`{{${key}}}`).join(value),
        template || ''
    );
}

// Same email-format regex used by the public lead form (components/public/LeadForm.jsx)
// — kept simple and permissive; the backend is the source of truth for real validation.
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function FieldError({ error }) {
    if (!error) return null;
    return <p className="mt-1 text-xs text-destructive">{error}</p>;
}

// The seven editable fields — used for dirty-state comparison
const EDITABLE_FIELDS = [
    'enabled',
    'sender_name',
    'sender_email',
    'subject_template',
    'body_template',
    'subject_template_sv',
    'body_template_sv',
];

function pickEditable(doc) {
    return EDITABLE_FIELDS.reduce((acc, key) => {
        acc[key] = doc[key] ?? null;
        return acc;
    }, {});
}

function isDirtyCheck(formState, savedState) {
    return EDITABLE_FIELDS.some((key) => formState[key] !== savedState[key]);
}

function formatDate(isoString) {
    if (!isoString) return null;
    try {
        return new Intl.DateTimeFormat('sv-SE', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(isoString));
    } catch {
        return isoString;
    }
}

export default function EmailNotificationsPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    // Full server doc — kept for metadata fields (updated_by_name, updated_at)
    const [serverDoc, setServerDoc] = useState(null);
    // Immutable snapshot of the last saved editable fields
    const [savedState, setSavedState] = useState(null);
    // Editable form state
    const [formState, setFormState] = useState(null);
    // Live rendered preview toggle (Finding 5.64a)
    const [showPreview, setShowPreview] = useState(false);
    // Real-time sender-email validation error (Finding 5.65a)
    const [senderEmailError, setSenderEmailError] = useState(null);

    const isDirty =
        formState && savedState ? isDirtyCheck(formState, savedState) : false;

    // Finding 5.64c — non-blocking warning when the body template omits the
    // personalization placeholder (the reminder would go out un-personalized).
    const missingUserNamePlaceholder =
        !!formState && !(formState.body_template || '').includes('{{user_name}}');
    // The Swedish body is optional (empty → English fallback), so only warn when
    // it is actually set but omits the personalization placeholder.
    const missingUserNamePlaceholderSv =
        !!formState &&
        !!(formState.body_template_sv || '').trim() &&
        !(formState.body_template_sv || '').includes('{{user_name}}');

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getEmailSettings();
            const doc = res.data;
            setServerDoc(doc);
            const editable = pickEditable(doc);
            setSavedState(editable);
            setFormState({ ...editable });
        } catch {
            toast.error(t('settings.email_notifications.failed_load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleFieldChange = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    // Finding 5.65a — real-time onBlur validation for the sender email field.
    // HTML5 type="email" only surfaces at submit; this gives immediate feedback.
    const handleSenderEmailBlur = () => {
        const value = (formState.sender_email || '').trim();
        setSenderEmailError(
            value && !EMAIL_FORMAT_REGEX.test(value)
                ? t('settings.email_notifications.sender_email_invalid')
                : null
        );
    };

    // Finding 5.64b — repopulate subject + body from the built-in defaults
    // (mirrors backend/models/email.py). Marks the form dirty so the admin
    // must explicitly Save (or Cancel to discard) before it takes effect.
    const handleResetToDefaults = () => {
        setFormState((prev) => ({
            ...prev,
            subject_template: DEFAULT_SUBJECT_TEMPLATE,
            body_template: DEFAULT_BODY_TEMPLATE,
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await updateEmailSettings(formState);
            const updatedDoc = res.data;
            setServerDoc(updatedDoc);
            const editable = pickEditable(updatedDoc);
            setSavedState(editable);
            setFormState({ ...editable });
            setSenderEmailError(null);  // valid settings persisted — clear any stale blur error
            toast.success(t('settings.email_notifications.settings_saved'));
        } catch (err) {
            toast.error(
                err.response?.data?.detail || t('settings.email_notifications.failed_save')
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormState({ ...savedState });
        setSenderEmailError(null);  // restoring saved state — discard any blur error
    };

    // Send one test email to the invoking admin using the CURRENT form state
    // (so unsaved changes are tested without having to Save first).
    const handleSendTest = async () => {
        setSendingTest(true);
        try {
            const res = await sendTestEmail({
                sender_name: formState.sender_name,
                sender_email: formState.sender_email,
                subject_template: formState.subject_template,
                body_template: formState.body_template,
            });
            const { status, to, error, reason } = res.data || {};
            if (status === 'ok') {
                toast.success(t('settings.email_notifications.test_sent', { to: to || 'you' }));
            } else if (status === 'skipped') {
                toast.warning(
                    t('settings.email_notifications.test_skipped', {
                        reason: reason === 'no_api_key'
                            ? t('settings.email_notifications.test_skipped_no_key')
                            : t('settings.email_notifications.test_skipped_other')
                    })
                );
            } else {
                toast.error(t('settings.email_notifications.test_failed', { error: error || 'unknown error' }));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t('settings.email_notifications.test_failed', { error: '' }));
        } finally {
            setSendingTest(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    if (!formState) {
        return (
            <div className="text-muted-foreground text-sm py-12 text-center">
                {t('settings.email_notifications.could_not_load')}
            </div>
        );
    }

    const isEnabled = formState.enabled === true;
    const lastEditedDate = serverDoc?.updated_at
        ? formatDate(serverDoc.updated_at)
        : null;
    const lastEditedBy = serverDoc?.updated_by_name || null;

    return (
        <TooltipProvider>
            <div className="space-y-6" data-testid="email-notifications-page">
                {/* 1. Heading + description */}
                <div>
                    <h2 className="font-heading text-2xl font-bold tracking-tight">
                        {t('settings.email_notifications.title')}
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        {t('settings.email_notifications.subtitle')}
                    </p>
                </div>

                {/* 2. Status banner */}
                {isEnabled ? (
                    <Alert className="border-emerald-200 bg-emerald-50/60 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        <AlertTitle className="text-emerald-700 dark:text-emerald-300">
                            {t('settings.email_notifications.active_title')}
                        </AlertTitle>
                        <AlertDescription className="text-emerald-700 dark:text-emerald-400">
                            {t('settings.email_notifications.active_description')}
                        </AlertDescription>
                    </Alert>
                ) : (
                    <Alert className="border-muted bg-muted/30 text-muted-foreground">
                        <MinusCircle className="h-4 w-4" />
                        <AlertTitle>{t('settings.email_notifications.disabled_title')}</AlertTitle>
                        <AlertDescription>
                            {t('settings.email_notifications.disabled_description')}
                        </AlertDescription>
                    </Alert>
                )}

                {/* 2b. Pending-changes indicator (Finding 5.65b) — the banner above
                    reflects the current (possibly unsaved) form state, not
                    necessarily what's persisted on the server. */}
                {isDirty && (
                    <p
                        className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400"
                        data-testid="email-settings-unsaved-indicator"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                        {t('settings.email_notifications.unsaved_changes')}
                    </p>
                )}

                {/* 3. Form card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">
                            {t('settings.email_notifications.card_title')}
                        </CardTitle>
                        <CardDescription>
                            {t('settings.email_notifications.card_description')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Enabled toggle */}
                        <div className="flex items-start gap-4">
                            <Switch
                                id="email-enabled"
                                checked={formState.enabled === true}
                                onCheckedChange={(checked) =>
                                    handleFieldChange('enabled', checked)
                                }
                                aria-label={t('settings.email_notifications.enable_aria')}
                            />
                            <div>
                                <Label
                                    htmlFor="email-enabled"
                                    className="text-sm font-medium leading-none cursor-pointer"
                                >
                                    {t('settings.email_notifications.enable_label')}
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('settings.email_notifications.enable_hint')}
                                </p>
                            </div>
                        </div>

                        <div className="border-t pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Sender Name */}
                            <div className="space-y-1">
                                <Label htmlFor="sender-name">
                                    {t('settings.email_notifications.sender_name_label')}
                                </Label>
                                <Input
                                    id="sender-name"
                                    value={formState.sender_name ?? ''}
                                    onChange={(e) =>
                                        handleFieldChange(
                                            'sender_name',
                                            e.target.value
                                        )
                                    }
                                    placeholder={t('settings.email_notifications.sender_name_placeholder')}
                                />
                            </div>

                            {/* Sender Email */}
                            <div className="space-y-1">
                                <Label htmlFor="sender-email">
                                    {t('settings.email_notifications.sender_email_label')}
                                </Label>
                                <Input
                                    id="sender-email"
                                    type="email"
                                    value={formState.sender_email ?? ''}
                                    onChange={(e) => {
                                        handleFieldChange(
                                            'sender_email',
                                            e.target.value
                                        );
                                        if (senderEmailError) setSenderEmailError(null);
                                    }}
                                    onBlur={handleSenderEmailBlur}
                                    aria-invalid={!!senderEmailError}
                                    placeholder={t('settings.email_notifications.sender_email_placeholder')}
                                />
                                <FieldError error={senderEmailError} />
                            </div>
                        </div>

                        {/* Subject template */}
                        <div className="space-y-1">
                            <Label htmlFor="subject-template">
                                {t('settings.email_notifications.subject_label')}
                            </Label>
                            <Input
                                id="subject-template"
                                value={formState.subject_template ?? ''}
                                onChange={(e) =>
                                    handleFieldChange(
                                        'subject_template',
                                        e.target.value
                                    )
                                }
                                placeholder={t('settings.email_notifications.subject_placeholder')}
                                className="font-mono text-sm"
                            />
                        </div>

                        {/* Body template */}
                        <div className="space-y-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <Label htmlFor="body-template">
                                    {t('settings.email_notifications.body_label')}{' '}
                                    <span className="text-muted-foreground font-normal">
                                        {t('settings.email_notifications.body_html_note')}
                                    </span>
                                </Label>
                                {/* Finding 5.64a/5.64b — live preview toggle + reset-to-defaults */}
                                <div className="flex items-center gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowPreview((prev) => !prev)}
                                        aria-label={t('settings.email_notifications.preview_toggle_aria')}
                                        aria-pressed={showPreview}
                                    >
                                        {showPreview ? (
                                            <EyeOff className="w-4 h-4 mr-1.5" />
                                        ) : (
                                            <Eye className="w-4 h-4 mr-1.5" />
                                        )}
                                        {showPreview
                                            ? t('settings.email_notifications.preview_hide')
                                            : t('settings.email_notifications.preview_show')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleResetToDefaults}
                                        aria-label={t('settings.email_notifications.reset_defaults_aria')}
                                    >
                                        <RotateCcw className="w-4 h-4 mr-1.5" />
                                        {t('settings.email_notifications.reset_defaults')}
                                    </Button>
                                </div>
                            </div>
                            <Textarea
                                id="body-template"
                                value={formState.body_template ?? ''}
                                onChange={(e) =>
                                    handleFieldChange(
                                        'body_template',
                                        e.target.value
                                    )
                                }
                                rows={15}
                                className="font-mono text-sm resize-y w-full"
                                placeholder={t('settings.email_notifications.body_placeholder')}
                            />
                            {/* Finding 5.64c — non-blocking warning; doesn't prevent saving */}
                            {missingUserNamePlaceholder && (
                                <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 mt-1">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                    {t('settings.email_notifications.missing_user_name_warning')}
                                </p>
                            )}
                            {/* Finding 5.64a — live rendered preview against sample data.
                                This box only ever renders body_template authored by the
                                logged-in admin viewing their own settings page — it is not
                                user-supplied content and is never shown to non-admins, so
                                dangerouslySetInnerHTML here is not an XSS surface. */}
                            {showPreview && (
                                <div
                                    className="mt-2 rounded-md border border-border bg-background p-4"
                                    data-testid="email-body-preview"
                                >
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                                        {t('settings.email_notifications.preview_label')}
                                    </p>
                                    <div
                                        className="text-sm leading-relaxed [&_p]:mb-3 [&_a]:underline"
                                        // eslint-disable-next-line react/no-danger -- admin-authored template, sandboxed to this admin's own preview box (see comment above)
                                        dangerouslySetInnerHTML={{
                                            __html: renderTemplatePreview(formState.body_template),
                                        }}
                                    />
                                </div>
                            )}
                            {/* Placeholder legend */}
                            <div className="mt-2 rounded-md bg-muted/40 border border-border px-4 py-3 text-xs text-muted-foreground space-y-1">
                                <p className="font-medium text-foreground mb-1">
                                    {t('settings.email_notifications.placeholders_title')}
                                </p>
                                <p>
                                    <code className="font-mono">
                                        {'{{user_name}}'}
                                    </code>{' '}
                                    &mdash; {t('settings.email_notifications.placeholder_user_name')}
                                </p>
                                <p>
                                    <code className="font-mono">
                                        {'{{month}}'}
                                    </code>{' '}
                                    &mdash; {t('settings.email_notifications.placeholder_month')}
                                </p>
                                <p>
                                    <code className="font-mono">
                                        {'{{year}}'}
                                    </code>{' '}
                                    &mdash; {t('settings.email_notifications.placeholder_year')}
                                </p>
                                <p>
                                    <code className="font-mono">
                                        {'{{submit_url}}'}
                                    </code>{' '}
                                    &mdash; {t('settings.email_notifications.placeholder_submit_url')}
                                </p>
                            </div>
                        </div>

                        {/* Swedish template overrides */}
                        <div className="border-t pt-6 space-y-4">
                            <div>
                                <p className="text-sm font-medium">
                                    Svenska (Swedish)
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t('settings.email_notifications.sv_section_hint', {
                                        defaultValue: 'Leave empty to use the English template as fallback for Swedish users.',
                                    })}
                                </p>
                            </div>

                            {/* Swedish subject */}
                            <div className="space-y-1">
                                <Label htmlFor="subject-template-sv">
                                    {t('settings.email_notifications.subject_label_sv', {
                                        defaultValue: 'Subject (Swedish)',
                                    })}
                                </Label>
                                <Input
                                    id="subject-template-sv"
                                    value={formState.subject_template_sv ?? ''}
                                    onChange={(e) =>
                                        handleFieldChange(
                                            'subject_template_sv',
                                            e.target.value || null,
                                        )
                                    }
                                    placeholder={t(
                                        'settings.email_notifications.subject_placeholder_sv',
                                        { defaultValue: 'Leave empty to use the English template as fallback' },
                                    )}
                                    className="font-mono text-sm"
                                />
                            </div>

                            {/* Swedish body */}
                            <div className="space-y-1">
                                <Label htmlFor="body-template-sv">
                                    {t('settings.email_notifications.body_label_sv', {
                                        defaultValue: 'Body (Swedish)',
                                    })}{' '}
                                    <span className="text-muted-foreground font-normal">
                                        {t('settings.email_notifications.body_html_note')}
                                    </span>
                                </Label>
                                <Textarea
                                    id="body-template-sv"
                                    value={formState.body_template_sv ?? ''}
                                    onChange={(e) =>
                                        handleFieldChange(
                                            'body_template_sv',
                                            e.target.value || null,
                                        )
                                    }
                                    rows={15}
                                    className="font-mono text-sm resize-y w-full"
                                    placeholder={t(
                                        'settings.email_notifications.body_placeholder_sv',
                                        { defaultValue: 'Leave empty to use the English template as fallback' },
                                    )}
                                />
                                {/* Finding 5.64c (SV) — non-blocking warning; only when the SV body is set */}
                                {missingUserNamePlaceholderSv && (
                                    <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 mt-1">
                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                        {t('settings.email_notifications.missing_user_name_warning')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 4. Footer row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Left — last edited metadata */}
                    <p className="text-xs text-muted-foreground">
                        {lastEditedBy && lastEditedDate
                            ? t('settings.email_notifications.last_edited_by', { name: lastEditedBy, date: lastEditedDate })
                            : t('settings.email_notifications.never_edited')}
                    </p>

                    {/* Right — action buttons */}
                    <div className="flex items-center gap-2">
                        {/* Send test email — sends one rendered reminder to the current admin's address */}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span tabIndex={0} className="inline-flex">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleSendTest}
                                        disabled={sendingTest || saving}
                                        aria-label={t('settings.email_notifications.send_test_aria')}
                                    >
                                        <FlaskConical className="w-4 h-4 mr-2" />
                                        {sendingTest ? t('settings.email_notifications.send_test_sending') : t('settings.email_notifications.send_test')}
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                {t('settings.email_notifications.send_test_tooltip')}
                            </TooltipContent>
                        </Tooltip>

                        <Button
                            type="button"
                            variant="ghost"
                            disabled={!isDirty}
                            onClick={handleCancel}
                            aria-label={t('settings.email_notifications.cancel_aria')}
                        >
                            <X className="w-4 h-4 mr-2" />
                            {t('common.cancel')}
                        </Button>

                        <Button
                            type="button"
                            disabled={!isDirty || saving}
                            onClick={handleSave}
                            aria-label={t('settings.email_notifications.save_aria')}
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}
